import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledEducationSection = styled.section`
  max-width: 900px;

  .schools {
    ${({ theme }) => theme.mixins.resetList};
    margin: 0 0 50px;
  }
`;

const StyledSchool = styled.li`
  &:not(:last-of-type) {
    margin-bottom: 30px;
  }

  h3 {
    margin: 0;
    color: var(--lightest-slate);
    font-size: var(--fz-xl);
    font-weight: 600;
    line-height: 1.3;
  }

  .degree {
    margin: 4px 0 0;
    color: var(--green);
    font-size: var(--fz-md);
  }

  .note {
    margin: 6px 0 0;
    color: var(--slate);
    font-size: var(--fz-md);
  }
`;

const StyledDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-gap: 30px;
  }

  h4 {
    margin: 0 0 15px;
    color: var(--lightest-slate);
    font-size: var(--fz-sm);
    font-weight: 400;
  }

  ul {
    ${({ theme }) => theme.mixins.fancyList};
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      font-size: var(--fz-md);
    }
  }

  .issuer {
    color: var(--slate);
  }
`;

const schools = [
  {
    name: 'University of Iowa',
    sub: 'Tippie College of Business — Iowa City, IA',
    degree: 'B.B.A., Finance and Risk Management & Insurance',
  },
  {
    name: 'Kirkwood Community College',
    sub: 'Cedar Rapids, IA',
    degree: 'Associate of Arts, Liberal Arts',
    note: 'Dean’s List',
  },
];

const coursework = [
  'Principles of Risk Management & Insurance',
  'Investment Management',
  'Introductory Financial Management',
  'Financial Accounting and Reporting',
  'Managerial Accounting Analytics & Data Visualization',
  'Foundations of Business Analytics',
];

// Order and wording as Wyatt specified. `issuer` is omitted where the issuing
// brand is already in the certificate's name.
const certifications = [
  { name: 'Bloomberg Market Concepts' },
  { name: 'Google Data Analytics Professional Certificate' },
  { name: 'Excel Skills for Business', issuer: 'Macquarie University' },
  { name: 'Google AI Essentials' },
  { name: 'Claude AI Fluency and Frameworks', issuer: 'Anthropic Academy' },
  { name: 'Claude 101', issuer: 'Anthropic Academy' },
];

const Education = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  return (
    <StyledEducationSection id="education" ref={revealContainer}>
      <h2 className="numbered-heading">Education</h2>

      <ul className="schools">
        {schools.map(({ name, sub, degree, note }, i) => (
          <StyledSchool key={i}>
            <h3>{name}</h3>
            <p className="degree">{degree}</p>
            <p className="note">
              {sub}
              {note && ` · ${note}`}
            </p>
          </StyledSchool>
        ))}
      </ul>

      <StyledDetails>
        <div>
          <h4>Coursework</h4>
          <ul>
            {coursework.map((course, i) => (
              <li key={i}>{course}</li>
            ))}
          </ul>
        </div>

        <div>
          <h4>Certifications</h4>
          <ul>
            {certifications.map(({ name, issuer }, i) => (
              <li key={i}>
                {name}
                {issuer && <span className="issuer"> · {issuer}</span>}
              </li>
            ))}
          </ul>
        </div>
      </StyledDetails>
    </StyledEducationSection>
  );
};

export default Education;
